import Phaser from 'phaser';
import i18next from 'i18next';
import { GAME_WIDTH, GAME_HEIGHT, FONT_STACK, RECIPE } from '@/game/config';
import { getBackground, getBestRecipe, setBestRecipe, type Background } from '@/game/storage';
import { fableVariant } from '@/game/theme';
import { Starfield } from '@/game/objects/Starfield';
import { addRoundedLabelBg } from '@/game/ui/roundedLabelBg';
import { addMuteToggle } from '@/game/ui/muteToggle';
import { scriptFontFamily } from '@/game/fonts';
import { getPictureWordsPool } from '@/data/pools';
import { maybeShowBuyPrompt } from '@/game/ui/buyPrompt';
import { maybeAskForRating } from '@/game/rating';
import { trackRecipeStart, trackRecipeEnd } from '@/analytics';
import {
  playIngredientSound,
  playDishSound,
  playThudSound,
  playRecordSound,
  playRoundCompleteSound,
  speakRulePrompt,
  startBackdropAmbience,
  stopBackdropAmbience,
} from '@/game/audio';
import { bandForRound, dealRound, type DealtRound } from '@/game/recipe/deal';
import { speakRoundIntro, speaksIntroOn } from '@/game/roundIntro';
import { recipesFor } from '@/game/recipe/recipes';
import {
  dishI18nKey, dishIconKey, dishIconUrl, ingredientI18nKey, ingredientIconKey, ingredientIconUrl,
} from '@/game/recipe/recipes';
import { redirectIfWebLimit } from '@/game/ui/webLimitCard';

interface RecipeInit {
  lang: string;
  /** Dev only (`?recipe=4`): start the ladder further up, where the card folds. */
  startRound?: number;
}

const CORN = '🌽';

/** The recipe card, top centre. Clear of the back pill on the left and the readouts on the right. */
const CARD = { x: GAME_WIDTH / 2, y: 92, w: 820, h: 136 };
/** The shelf the tiles are laid out in. */
const SHELF = { x: 190, y: 196, w: 900, h: 300 };
/**
 * The bowl everything falls into. `y` is its RIM, and the bowl hangs below it: the slice is drawn
 * from the rim down, so the lowest pixel is `y + r`. Placed so that bottom edge clears the flame.
 */
const BOWL = { x: GAME_WIDTH / 2, y: 556, r: 92 };
/** The flame under it — this mode's clock. Sits below the bowl's lowest point (556 + 92), not on it. */
const FLAME = { x: GAME_WIDTH / 2, y: 682, w: 420, h: 16 };
/** Fraction of the flame left at which the bar turns red, whatever the card is doing (see update). */
const FLAME_RED_AT = 0.25;

const TILE_FILL = 0xf2f6fa;
const TILE_EDGE = 0x8fb6d8;

interface Tile {
  id: string;
  root: Phaser.GameObjects.Container;
  /** Shown only when this tile has just been tapped wrongly — the word that cost a corn (§5.2). */
  name: Phaser.GameObjects.Text;
  taken: boolean;
}

/**
 * Recipe Kitchen — the eclipse reward minigame (specs/recipe-mode.md).
 *
 * A recipe card is written out in words; the shelf below it is pictures with no text on them. Tap
 * the ingredients into the bowl and the dish comes up on a plate. A wrong tile or a burnt-out flame
 * costs a 🌽 of five.
 *
 * The mode's one idea is the DIRECTION: every other board in the game answers with a word, and this
 * one asks with words and answers with a picture — and asks for a set rather than an item, so the
 * child has to hold the list. Self-contained like the other reward scenes: it touches neither
 * RoundScene/BoardScene nor the Leitner engine, and is entered only by spending a banked Plate.
 * What is on the shelf is decided by the Phaser-free dealer in game/recipe/deal.ts.
 */
export class RecipeScene extends Phaser.Scene {
  private lang!: string;
  private startRound = 1;

  /** Ingredient id → the word for it in this language (picture pool first, then this mode's own). */
  private words = new Map<string, string>();

  private round = 1;
  private corn = RECIPE.startCorn;
  private dishes = 0;
  private best = 0;

  private used = new Set<string>();
  /**
   * Distinct dishes actually COOKED this run — not the same thing as `used`, which the dealer
   * clears whenever a band empties. This one only grows, and filling it is what earns the
   * master-chef card (§5.5).
   */
  private cooked = new Set<string>();
  /** The dish just cooked, kept out of the next deal so nothing ever comes back-to-back. */
  private lastRecipe: string | undefined;
  /** The master-chef card is a once-per-run moment. */
  private chefCelebrated = false;
  private current: DealtRound | null = null;
  private tiles: Tile[] = [];
  private bowl: string[] = [];
  /** Tiles already charged for this round — a second tap on the onion is bookkeeping (§5.2). */
  private charged = new Set<string>();
  private cleanRound = true;
  /**
   * Is this round's dish already on its way to the plate?
   *
   * The bowl fills at TAP time but each ingredient reaches it at the end of its own arc, so the
   * last taps all land on an already-complete bowl and every one of their tweens asks to serve.
   * Without this the counter jumped by three on a three-ingredient recipe.
   */
  private serving = false;

  private cardTitle!: Phaser.GameObjects.Text;
  private chips: Phaser.GameObjects.Text[] = [];
  private chipsBox!: Phaser.GameObjects.Container;
  private cardHint!: Phaser.GameObjects.Text;
  private folded = false;
  private foldTimer?: Phaser.Time.TimerEvent;

  private flameTimer?: Phaser.Time.TimerEvent;
  private flameBar!: Phaser.GameObjects.Graphics;
  private bowlPile!: Phaser.GameObjects.Container;

  private cornIcons: Phaser.GameObjects.Text[] = [];
  private roundText!: Phaser.GameObjects.Text;
  private servedText!: Phaser.GameObjects.Text;

  private plateTexKeys: string[] = [];
  private locked = true;      // stays locked until the how-to card is dismissed
  private over = false;

  constructor() {
    super({ key: 'Recipe' });
  }

  init(data: RecipeInit): void {
    this.lang = data.lang;
    this.startRound = Math.max(1, Math.floor(data.startRound ?? 1));
    this.round = this.startRound;
    this.corn = RECIPE.startCorn;
    this.dishes = 0;
    this.used = new Set();
    this.cooked = new Set();
    this.lastRecipe = undefined;
    this.chefCelebrated = false;
    this.current = null;
    this.tiles = [];
    this.bowl = [];
    this.charged = new Set();
    this.cleanRound = true;
    this.serving = false;
    this.folded = false;
    this.locked = true;
    this.over = false;
  }

  preload(): void {
    // The player's chosen backdrop, loaded on demand exactly as PairsScene does it.
    const bg = getBackground();
    if (bg !== 'space') {
      const asset = backgroundAsset(bg);
      if (!this.textures.exists(`bg_${asset}`)) {
        this.load.image(`bg_${asset}`, `/images/background_${asset}.jpg`);
      }
    }
  }

  create(): void {
    if (redirectIfWebLimit(this)) return;   // free web build used up — see webLimitCard.ts
    this.best = getBestRecipe();
    trackRecipeStart();   // web demo only

    this.drawBackdrop();
    startBackdropAmbience(getBackground());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      stopBackdropAmbience();
      this.clearPlateTextures();
    });

    // A hand, not the crosshair: nothing here is aimed at, everything is tapped (see the same note
    // in PairsScene). MenuScene.create sets it back on the way out.
    this.input.setDefaultCursor('pointer');

    this.words = this.buildWords();
    this.drawHud();
    this.drawCard();
    this.drawBowl();
    this.drawFlame();

    addMuteToggle(this, {
      depth: 50,
      onToggle: (muted) => {
        if (muted) stopBackdropAmbience();
        else startBackdropAmbience(getBackground());
      },
    });

    this.showHowTo(() => this.startRoundNow(this.round));
  }

  // ── content ──────────────────────────────────────────────────────────────────

  /**
   * Every word this run can print, resolved once.
   *
   * Two sources, one rule (recipes.ts): the picture pool owns the words it already has — so a child
   * reads the same *Tomate* here as in the picture lesson — and this mode's own eight come from its
   * own i18n keys. A pool word that somehow has no text falls back to its id, which is ugly on
   * purpose: silence would look like an empty card.
   */
  private buildWords(): Map<string, string> {
    const out = new Map<string, string>();
    const pool = getPictureWordsPool(this.lang) ?? getPictureWordsPool('en');
    for (const set of pool?.sets ?? []) {
      for (const w of set.words) {
        if (typeof w.id === 'string' && typeof w.text === 'string') out.set(w.id, w.text);
      }
    }
    return out;
  }

  private nameOf(id: string): string {
    const key = ingredientI18nKey(id);
    if (key) return i18next.t(key, { defaultValue: id });
    return this.words.get(id) ?? id;
  }

  // ── rounds ───────────────────────────────────────────────────────────────────

  private startRoundNow(round: number): void {
    this.round = round;
    this.locked = true;
    this.bowl = [];
    this.charged = new Set();
    this.cleanRound = true;
    this.serving = false;
    this.roundText.setText(i18next.t('recipe.round', { n: round }));

    const dealt = dealRound(round, this.used, Math.random, this.lastRecipe, this.lang);
    this.beginRound(dealt);
  }

  /**
   * Load what this round shows and lay it out.
   *
   * One caller and one path now. This used to take a `keepRecipe` and deal repeatedly until that
   * dish came up again, for the burnt round — a loop that also dealt without a language, so a
   * German re-deal could land on gimbap. Both problems left with the re-deal itself (see `burn`).
   */
  private beginRound(round: DealtRound): void {
    this.current = round;

    // Load only what this round shows — precached by the service worker, so a cache read rather
    // than a request (the same load-what-you-show policy as BoardScene's picture banners).
    const assets: { key: string; url: string }[] = [];
    for (const id of round.shelf) {
      if (!this.textures.exists(ingredientIconKey(id))) {
        assets.push({ key: ingredientIconKey(id), url: ingredientIconUrl(id) });
      }
    }
    if (!this.textures.exists(dishIconKey(round.recipe.id))) {
      assets.push({ key: dishIconKey(round.recipe.id), url: dishIconUrl(round.recipe.id) });
    }
    if (assets.length === 0) {
      this.layoutRound();
      return;
    }
    for (const a of assets) this.load.image(a.key, a.url);
    // COMPLETE fires even when some files failed; a tile whose texture is missing draws a
    // placeholder rather than an empty box (see makeTile).
    this.load.once(Phaser.Loader.Events.COMPLETE, () => this.layoutRound());
    this.load.start();
  }

  private layoutRound(): void {
    const round = this.current;
    if (!round) return;

    for (const t of this.tiles) if (t.root.active) t.root.destroy();
    this.tiles = [];
    this.clearPlateTextures();
    this.bowlPile.removeAll(true);

    this.paintCard();

    const n = round.shelf.length;
    const cols = Math.min(6, Math.ceil(n / Math.ceil(n / 6)));
    const rows = Math.ceil(n / cols);
    const cellW = SHELF.w / cols;
    const cellH = SHELF.h / rows;
    // A finger, not a mouse: the tile is the cell minus a gutter, and the picture is inset again.
    const tileW = Math.min(cellW - 18, 168);
    const tileH = Math.min(cellH - 18, 150);

    round.shelf.forEach((id, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      // Centre the last row when it is short, so a 9-tile shelf does not sit lopsided.
      const inRow = Math.min(cols, n - row * cols);
      const rowW = inRow * cellW;
      const x = SHELF.x + (SHELF.w - rowW) / 2 + cellW * (col + 0.5);
      const y = SHELF.y + cellH * (row + 0.5);
      const tile = this.makeTile(id, x, y, tileW, tileH);
      this.tiles.push(tile);
      tile.root.setScale(0.2).setAlpha(0);
      this.tweens.add({
        targets: tile.root, scale: 1, alpha: 1, duration: 220, delay: i * 24, ease: 'Back.easeOut',
      });
    });

    // The board's one instruction, shown AND spoken for the first few rounds rather than only in
    // the how-to card: that card is read once at the start of a run, and a child who taps past it
    // has nothing. Stopping early is the other half — a sentence that arrives every round with
    // nothing new to say stops being read and starts being in the way.
    //
    // **What is spoken is the instruction, never the card.** §4.2's rule stands: reading the
    // ingredient list aloud would answer the board for a child who cannot yet read it, which is the
    // one thing this mode is for. "Wähle alle Zutaten" tells them what to do and nothing about
    // what. The dish name is still spoken at the other end, when the dish is already on the plate.
    //
    // Printed and spoken share `speaksIntroOn` rather than each keeping their own 3: the six modes
    // agree on what "the first few rounds" means (game/roundIntro.ts), and here the banner and the
    // voice can never drift apart from each other either.
    if (speaksIntroOn(this.round)) {
      const ask = i18next.t('recipe.chooseAll');
      this.banner(ask, '#ffd166');
      speakRoundIntro(ask, this.lang, this.round);
    }

    this.time.delayedCall(n * 24 + 240, () => {
      if (this.over) return;
      this.locked = false;
      this.startFlame();
      this.armFold();
    });
  }

  // ── the card ─────────────────────────────────────────────────────────────────

  private drawCard(): void {
    const bg = this.add.graphics().setDepth(9);
    bg.fillStyle(0xfdf6e6, 0.96).fillRoundedRect(CARD.x - CARD.w / 2, CARD.y - CARD.h / 2, CARD.w, CARD.h, 18);
    bg.lineStyle(3, 0xd9b98a, 0.95).strokeRoundedRect(CARD.x - CARD.w / 2, CARD.y - CARD.h / 2, CARD.w, CARD.h, 18);

    // Plain on its own plate. `wordTextStyle` is for words flying over a photo backdrop and carries
    // a 6px outline; on this cream card that outline reads as fat, over-inked lettering and the
    // words get harder to read, not easier — the note on scriptFontFamily in fonts.ts is about
    // exactly this, and the Ice Maze's tiles learned it first. The face alone is what a plate wants,
    // so Thai, Japanese and Korean still shape correctly.
    this.cardTitle = this.add.text(CARD.x, CARD.y - 38, '', {
      fontFamily: scriptFontFamily(this.lang), fontSize: '40px', color: '#3a2a18',
    }).setOrigin(0.5).setDepth(10);

    this.chipsBox = this.add.container(CARD.x, CARD.y + 26).setDepth(10);

    // Only ever visible while the card is folded — the invitation to look again (§4.3).
    this.cardHint = this.add.text(CARD.x, CARD.y + 26, '👆', {
      fontFamily: FONT_STACK, fontSize: '22px',
    }).setOrigin(0.5).setDepth(10).setAlpha(0);

    // The whole card is the peek button, not a small corner of it: a folded card is exactly the
    // thing a child wants to prod, and the target should be as big as the object.
    const zone = this.add.zone(CARD.x, CARD.y, CARD.w, CARD.h).setInteractive({ useHandCursor: true }).setDepth(11);
    zone.on('pointerdown', () => this.peek(true));
    zone.on('pointerup', () => this.peek(false));
    zone.on('pointerout', () => this.peek(false));
  }

  private paintCard(): void {
    const round = this.current;
    if (!round) return;

    this.cardTitle.setText(i18next.t(dishI18nKey(round.recipe.id), { defaultValue: round.recipe.id }));

    // removeAll(true), not a walk over `this.chips`: the box has held more than the chips before —
    // a strike-through line per crossed-off ingredient, which was never in that array and so
    // survived into the next round, drawn across words the child had not taken yet.
    this.chipsBox.removeAll(true);
    this.chips = [];
    this.folded = false;
    this.chipsBox.setAlpha(1);
    this.cardHint.setAlpha(0);

    // Laid out in one row and measured as they are built, so ten-letter German compounds and Thai
    // clusters both stay inside the card.
    const gap = 26;
    const texts = round.recipe.ingredients.map((id) => {
      const t = this.add.text(0, 0, this.nameOf(id), {
        fontFamily: scriptFontFamily(this.lang), fontSize: '30px', color: '#3a2a18',
      }).setOrigin(0.5);
      t.setData('id', id);
      return t;
    });
    let size = 30;
    const widthOf = (): number => texts.reduce((w, t) => w + t.width, 0) + gap * (texts.length - 1);
    while (size > 18 && widthOf() > CARD.w - 60) {
      size -= 2;
      for (const t of texts) t.setFontSize(size);
    }
    let x = -widthOf() / 2;
    for (const t of texts) {
      t.setPosition(x + t.width / 2, 0);
      x += t.width + gap;
      this.chipsBox.add(t);
      this.chips.push(t);
    }
  }

  /**
   * Mark an ingredient as taken: the card is also the progress display, so nothing has to be
   * counted.
   *
   * Dimmed, and deliberately NOT struck through. A line across a word is the one mark a child this
   * age meets as "wrong" — on a card whose whole job is to be read, it reads as a correction rather
   * than as a tick, and it makes the word harder to read at exactly the moment they may want to
   * re-check it.
   */
  private crossOff(id: string): void {
    const chip = this.chips.find((c) => c.getData('id') === id && c.alpha === 1);
    if (!chip) return;
    chip.setAlpha(0.34);
  }

  private armFold(): void {
    this.foldTimer?.remove();
    const band = bandForRound(this.round);
    if (!band.folds) return;
    this.foldTimer = this.time.delayedCall(band.foldAfterMs, () => {
      if (this.over) return;
      this.folded = true;
      this.tweens.add({ targets: this.chipsBox, alpha: 0, duration: 260 });
      this.tweens.add({ targets: this.cardHint, alpha: 0.75, duration: 260 });
    });
  }

  /** Hold the card to read it again, as often as you like. It costs clock and nothing else. */
  private peek(open: boolean): void {
    if (!this.folded || this.over) return;
    this.chipsBox.setAlpha(open ? 1 : 0);
    this.cardHint.setAlpha(open ? 0 : 0.75);
  }

  // ── the shelf ────────────────────────────────────────────────────────────────

  private tilePlate(w: number, h: number, fill: number): string {
    const cw = Math.round(w);
    const ch = Math.round(h);
    const key = `recipe-plate-${cw}x${ch}-${fill.toString(16)}`;
    if (this.textures.exists(key)) return key;
    const pad = 2;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(fill, 0.97).fillRoundedRect(pad, pad, cw, ch, 16);
    g.lineStyle(2, TILE_EDGE, 0.9).strokeRoundedRect(pad, pad, cw, ch, 16);
    g.generateTexture(key, cw + pad * 2, ch + pad * 2);
    g.destroy();
    this.plateTexKeys.push(key);
    return key;
  }

  private clearPlateTextures(): void {
    for (const key of this.plateTexKeys) this.textures.remove(key);
    this.plateTexKeys = [];
  }

  private makeTile(id: string, x: number, y: number, w: number, h: number): Tile {
    const root = this.add.container(x, y).setDepth(12);
    root.add(this.add.image(0, 0, this.tilePlate(w, h, TILE_FILL)));

    const key = ingredientIconKey(id);
    if (this.textures.exists(key)) {
      const img = this.add.image(0, 0, key);
      const size = Math.min(w, h) - 20;
      img.setDisplaySize(size, size);
      root.add(img);
    } else {
      root.add(this.add.text(0, 0, '🥄', {
        fontFamily: FONT_STACK, fontSize: `${Math.round(Math.min(w, h) * 0.4)}px`,
      }).setOrigin(0.5));
    }

    // The word a wrong tap costs a corn for. Built now, shown for a beat then (§5.2) — the tile
    // teaches what was tapped, never what was missing.
    const name = this.add.text(0, h / 2 - 16, this.nameOf(id), {
      fontFamily: scriptFontFamily(this.lang), fontSize: '22px', color: '#3a2a18',
      backgroundColor: '#ffffffcc', padding: { x: 8, y: 3 },
    }).setOrigin(0.5).setAlpha(0);
    root.add(name);

    // Explicit hit rectangle rather than setSize()+setInteractive(): a container's displayOrigin
    // quirk once left the lower half of a hit-area un-clickable (see BoardScene.addHelpButton).
    root.setInteractive(
      new Phaser.Geom.Rectangle(-w / 2, -h / 2, w, h),
      Phaser.Geom.Rectangle.Contains,
    );
    root.input!.cursor = 'pointer';

    const tile: Tile = { id, root, name, taken: false };
    root.on('pointerdown', () => this.onTileTap(tile));
    return tile;
  }

  private onTileTap(tile: Tile): void {
    if (this.over || this.locked || tile.taken) return;
    const round = this.current;
    if (!round) return;

    if (round.recipe.ingredients.includes(tile.id) && !this.bowl.includes(tile.id)) {
      this.takeIngredient(tile);
      return;
    }
    this.wrongTile(tile);
  }

  private takeIngredient(tile: Tile): void {
    tile.taken = true;
    tile.root.disableInteractive();
    playIngredientSound(this.bowl.length);
    this.bowl.push(tile.id);
    this.crossOff(tile.id);

    // Arc it into the bowl, then leave it there as part of the pile.
    this.tweens.add({
      targets: tile.root,
      x: BOWL.x + (Math.random() * 60 - 30),
      y: BOWL.y - 10,
      scale: 0.34,
      angle: Math.random() * 40 - 20,
      duration: 420,
      ease: 'Quad.easeIn',
      onComplete: () => {
        // Out of the list as well as off the screen. A destroyed container left in `this.tiles` is
        // a landmine: anything that later walks the shelf — gameOver disabling it, a re-layout —
        // touches a GameObject whose `scene` is gone and throws `undefined (reading 'sys')`. That
        // throw happens inside a timer or tween callback, which takes Phaser's whole game loop with
        // it: the screen stops responding entirely, back button included. It read as "the recipe
        // game freezes sometimes", and the "sometimes" was simply whether an ingredient had been
        // taken before the run ended.
        const at = this.tiles.indexOf(tile);
        if (at >= 0) this.tiles.splice(at, 1);
        tile.root.destroy();
        this.addToPile(tile.id);
        this.checkDish();
      },
    });
  }

  private addToPile(id: string): void {
    const key = ingredientIconKey(id);
    const i = this.bowl.length - 1;
    const x = -46 + (i % 3) * 46 + (Math.random() * 10 - 5);
    const y = 6 - Math.floor(i / 3) * 26;
    const piece = this.textures.exists(key)
      ? this.add.image(x, y, key).setDisplaySize(52, 52)
      : this.add.text(x, y, '🥄', { fontFamily: FONT_STACK, fontSize: '30px' }).setOrigin(0.5);
    this.bowlPile.add(piece);
    this.tweens.add({ targets: this.bowlPile, scaleY: 0.9, duration: 90, yoyo: true });
  }

  private wrongTile(tile: Tile): void {
    // Charged once per tile per round, and never in the flame's last moments — a tap that lands as
    // the clock runs out is charged to the clock, not twice (§5.2).
    const late = (this.flameTimer?.getRemaining() ?? Infinity) <= RECIPE.graceMs;
    const alreadyCharged = this.charged.has(tile.id);

    tile.name.setAlpha(1);
    this.tweens.add({
      targets: tile.root, x: tile.root.x + 8, duration: 55, yoyo: true, repeat: 2, ease: 'Sine.easeInOut',
    });
    this.time.delayedCall(RECIPE.wrongNameMs, () => {
      if (tile.root.active) tile.name.setAlpha(0);
    });

    if (alreadyCharged || late) return;
    this.charged.add(tile.id);
    this.cleanRound = false;
    playThudSound();
    this.loseCorn();
  }

  private checkDish(): void {
    const round = this.current;
    if (!round || this.over || this.serving) return;
    if (!round.recipe.ingredients.every((id) => this.bowl.includes(id))) return;
    this.serving = true;
    this.serveDish();
  }

  // ── the flame ────────────────────────────────────────────────────────────────

  private drawFlame(): void {
    this.add.text(FLAME.x - FLAME.w / 2 - 30, FLAME.y, '🔥', {
      fontFamily: FONT_STACK, fontSize: '26px',
    }).setOrigin(0.5).setDepth(20);
    this.flameBar = this.add.graphics().setDepth(20);
  }

  private startFlame(): void {
    this.flameTimer?.remove();
    this.flameTimer = this.time.delayedCall(bandForRound(this.round).ms, () => this.burn());
  }

  update(): void {
    if (!this.flameBar) return;
    const left = this.flameTimer && !this.over ? 1 - this.flameTimer.getProgress() : 0;
    const g = this.flameBar;
    g.clear();
    g.fillStyle(0x000000, 0.35).fillRoundedRect(FLAME.x - FLAME.w / 2, FLAME.y - FLAME.h / 2, FLAME.w, FLAME.h, 8);
    if (left <= 0) return;

    /**
     * The bar answers "how worried should I be", and the honest answer depends on the CARD before
     * it depends on the clock: while the ingredients are still written out there is nothing to
     * hurry about, so the bar is calm blue however much time has gone. It turns yellow when the
     * words fold away and the child is working from memory, and red at the end whatever the card is
     * doing. Rounds 1–2 never fold, so they run blue straight into red — which is right, since
     * nothing was taken away from them.
     */
    const colour = left <= FLAME_RED_AT ? 0xff6a12 : this.folded ? 0xffd166 : 0x7ec4ff;
    g.fillStyle(colour, 0.95)
      .fillRoundedRect(FLAME.x - FLAME.w / 2, FLAME.y - FLAME.h / 2, FLAME.w * left, FLAME.h, 8);
  }

  /**
   * Time up: the bowl tips, one corn goes dark, and the run ENDS on its result card (§5.3).
   *
   * It used to re-deal the same recipe, which read well on paper and was the mode's one way to
   * strand a child: the re-deal ran through a loader and a retry loop, and anything that went wrong
   * in there left the board locked with no dish, no clock and nothing to tap. A screen that has
   * stopped asking for anything must never be a screen you cannot leave — so the burnt round now
   * goes somewhere by itself, and `gameOver` takes it back to the menu on a timer as well as on a
   * tap (RECIPE.autoLeaveBurnMs).
   */
  private burn(): void {
    if (this.over) return;
    this.locked = true;
    this.flameTimer = undefined;
    this.foldTimer?.remove();
    playThudSound();
    this.banner(i18next.t('recipe.timeUp'), '#ff8a5c');
    this.loseCorn();
    // loseCorn ends the run itself when that was the last corn; don't schedule it twice.
    if (this.over) return;
    this.time.delayedCall(RECIPE.burnToEndMs, () => this.gameOver(RECIPE.autoLeaveBurnMs));
  }

  // ── the dish ─────────────────────────────────────────────────────────────────

  private drawBowl(): void {
    const g = this.add.graphics().setDepth(14);
    // A plain bowl: a filled half-round with a rim. Drawn rather than an image, like everything
    // else in the reward scenes — the art budget belongs to the ingredients.
    g.fillStyle(0x2b3440, 0.95);
    g.slice(BOWL.x, BOWL.y, BOWL.r, 0, Math.PI, false);
    g.fillPath();
    g.lineStyle(5, 0x7ec4ff, 0.9);
    g.beginPath();
    g.arc(BOWL.x, BOWL.y, BOWL.r, 0, Math.PI, false);
    g.strokePath();
    g.lineBetween(BOWL.x - BOWL.r - 10, BOWL.y, BOWL.x + BOWL.r + 10, BOWL.y);

    // The pile sits BEHIND the bowl's front wall, so an ingredient reads as being in it.
    this.bowlPile = this.add.container(BOWL.x, BOWL.y).setDepth(13);
  }

  private serveDish(): void {
    if (this.over) return;
    this.locked = true;

    // Read the clock BEFORE stopping it: Phaser's TimerEvent.remove() sets `elapsed = delay`, so a
    // flame measured after the stop always reads empty — and the corn-back bonus of §5.4 could
    // never have fired.
    const round = this.current!;
    const flameLeft = 1 - (this.flameTimer?.getProgress() ?? 1);
    this.flameTimer?.remove();
    this.foldTimer?.remove();
    this.dishes += 1;
    this.servedText.setText(i18next.t('recipe.cleared', { n: this.dishes }));
    playDishSound();

    const name = i18next.t(dishI18nKey(round.recipe.id), { defaultValue: round.recipe.id });
    // The dish speaks its own name — the mode's teaching moment, and the one place a word is read
    // ALOUD to the child. The ingredient list never is: that would answer the question (§4.2).
    speakRulePrompt(name, this.lang);

    this.bowlPile.removeAll(true);
    this.cooked.add(round.recipe.id);
    this.lastRecipe = round.recipe.id;

    const layer = this.add.container(0, 0).setDepth(120);
    // A scrim under the plate: without it the dish comes up among the leftover shelf tiles and the
    // payoff of the round reads as one more picture on a busy screen.
    const scrim = this.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x0d1117, 0.62,
    );
    const key = dishIconKey(round.recipe.id);
    const art = this.textures.exists(key)
      ? this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 20, key).setDisplaySize(260, 260)
      : this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 20, '🍽', {
          fontFamily: FONT_STACK, fontSize: '160px',
        }).setOrigin(0.5);
    const label = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 150, name, {
      fontFamily: scriptFontFamily(this.lang), fontSize: '48px', color: '#ffd166', align: 'center',
      padding: { x: 26, y: 14 },
    }).setOrigin(0.5);
    const labelBg = addRoundedLabelBg(this, label, { radius: 20 });
    layer.add([scrim, art, labelBg, label]);
    layer.setAlpha(0);
    this.tweens.add({ targets: layer, alpha: 1, duration: 260 });
    this.tweens.add({
      targets: art, scale: { from: (art as Phaser.GameObjects.Image).scale * 0.7, to: (art as Phaser.GameObjects.Image).scale },
      duration: 320, ease: 'Back.easeOut',
    });

    // A clean dish with time to spare hands a corn back (§5.4) — why a strong run can go long.
    if (this.cleanRound && flameLeft >= RECIPE.cornBackAtFlame && this.corn < RECIPE.startCorn) {
      this.corn += 1;
      this.paintCorn();
      const back = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 214, i18next.t('recipe.cornBack'), {
        fontFamily: FONT_STACK, fontSize: '30px', color: '#4ecdc4',
      }).setOrigin(0.5);
      layer.add(back);
    }

    // Held, unless a tap cuts it short — the same impatience rule as the Pairs mismatch hold.
    // This language's book, not the whole file: a Thai run has three dishes a German one never
    // sees, so "all of them" is a different number per language (§5.5).
    const everyRecipe = this.cooked.size >= recipesFor(this.lang).length && !this.chefCelebrated;
    let moved = false;
    const next = (): void => {
      if (moved) return;
      moved = true;
      this.input.off('pointerdown', next);
      layer.destroy(true);
      if (this.over) return;
      // The whole book, in one run. The card comes AFTER the dish that finished it, so the child
      // sees the last plate and then hears what it meant.
      if (everyRecipe) {
        this.chefCelebrated = true;
        this.showChefCard(() => this.startRoundNow(this.round + 1));
        return;
      }
      this.startRoundNow(this.round + 1);
    };
    this.time.delayedCall(RECIPE.dishHoldMs, next);
    this.time.delayedCall(500, () => { if (!moved) this.input.once('pointerdown', next); });
  }

  // ── lives ────────────────────────────────────────────────────────────────────

  private loseCorn(): void {
    this.corn = Math.max(0, this.corn - 1);
    this.paintCorn();
    if (this.corn === 0) {
      this.locked = true;
      this.time.delayedCall(650, () => this.gameOver());
    }
  }

  /** An eaten corn is the same glyph dimmed — there is no hollow-corn character (cf. ♥/♡). */
  private paintCorn(): void {
    this.cornIcons.forEach((c, i) => c.setAlpha(i < this.corn ? 1 : 0.3));
  }

  // ── HUD ──────────────────────────────────────────────────────────────────────

  private drawBackdrop(): void {
    const bg = getBackground();
    if (bg === 'space') {
      new Starfield(this);
      return;
    }
    const key = `bg_${backgroundAsset(bg)}`;
    if (this.textures.exists(key)) {
      const img = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, key);
      img.setScale(Math.max(GAME_WIDTH / img.width, GAME_HEIGHT / img.height));
      img.setDepth(-1);
    } else {
      new Starfield(this);
    }
  }

  private drawHud(): void {
    // Corner for corner with the round mode's HUD, exactly as PairsScene lays itself out: the back
    // pill top-left with the lives directly under it, the run's readouts top-right. A child moving
    // between the lesson and this reward should not have to re-find what a life looks like.
    const back = this.drawBackButton();

    const cornY = back.y + back.height + 10;
    this.cornIcons = [];
    for (let i = 0; i < RECIPE.startCorn; i++) {
      this.cornIcons.push(this.add.text(back.x + i * 40, cornY, CORN, {
        fontFamily: FONT_STACK, fontSize: '32px',
      }).setDepth(40));
    }
    this.paintCorn();

    this.roundText = this.add.text(GAME_WIDTH - 24, 20, i18next.t('recipe.round', { n: this.round }), {
      fontFamily: FONT_STACK, fontSize: '28px', color: '#ffd166',
    }).setOrigin(1, 0).setDepth(40);

    this.servedText = this.add.text(GAME_WIDTH - 24, 58, i18next.t('recipe.cleared', { n: 0 }), {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#ffffff',
    }).setOrigin(1, 0).setDepth(40);
  }

  private drawBackButton(): Phaser.GameObjects.Text {
    const back = this.add.text(12, 16, `← ${i18next.t('nav.menu')}`, {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#dfe6ee', padding: { x: 20, y: 12 },
    }).setOrigin(0, 0).setDepth(50);
    back.setInteractive({
      hitArea: new Phaser.Geom.Rectangle(
        -back.x, -back.y, back.width + back.x + 10, back.height + back.y + 10,
      ),
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      useHandCursor: true,
    });
    addRoundedLabelBg(this, back);
    back.on('pointerover', () => back.setColor('#ffffff'));
    back.on('pointerout', () => back.setColor('#dfe6ee'));
    // Straight out, no confirm — the same call PairsScene makes and for the same reason.
    back.on('pointerdown', () => this.scene.start('Menu'));
    return back;
  }

  private banner(text: string, colour: string): void {
    const t = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, text, {
      fontFamily: FONT_STACK, fontSize: '52px', color: colour, align: 'center',
      padding: { x: 30, y: 18 },
    }).setOrigin(0.5).setDepth(100);
    const bg = addRoundedLabelBg(this, t, { radius: 22 });
    this.time.delayedCall(1100, () => { t.destroy(); bg.destroy(); });
  }

  // ── overlays ─────────────────────────────────────────────────────────────────

  private showHowTo(onStart: () => void): void {
    // Built to HelpScene's panel, plate for plate, like every other reward mode's card.
    const layer = this.add.container(0, 0).setDepth(200);
    const shade = this.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.85,
    );
    shade.setInteractive();
    const panel = this.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH * 0.85, GAME_HEIGHT * 0.78, 0x1a1f25,
    ).setStrokeStyle(4, 0xffd166);
    const title = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 210, i18next.t('recipe.howToTitle'), {
      fontFamily: FONT_STACK, fontSize: '64px', color: '#ffd166', fontStyle: 'bold',
    }).setOrigin(0.5);
    const body = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 30, i18next.t('recipe.howToBody'), {
      fontFamily: FONT_STACK, fontSize: '28px', color: '#ffffff', align: 'center',
      wordWrap: { width: GAME_WIDTH * 0.72 }, lineSpacing: 8,
    }).setOrigin(0.5);
    const corn = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 90, CORN.repeat(RECIPE.startCorn), {
      fontFamily: FONT_STACK, fontSize: '38px',
    }).setOrigin(0.5);
    const start = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 200, i18next.t('recipe.howToStart'), {
      fontFamily: FONT_STACK, fontSize: '36px', color: '#ffffff',
      backgroundColor: '#7ec4ff', padding: { x: 40, y: 14 }, fontStyle: 'bold',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    layer.add([shade, panel, title, body, corn, start]);

    // Tap anywhere to start, behind a timestamp guard so the tap that spent the Plate cannot skip
    // the card it just opened. A timestamp rather than a delayed listener, because a scene whose
    // clock is stalled (a backgrounded tab) would otherwise be stuck here (see PairsScene).
    const openedAt = performance.now();
    const dismiss = (): void => {
      if (performance.now() - openedAt < 350) return;
      this.input.off('pointerdown', dismiss);
      layer.destroy(true);
      onStart();
    };
    this.input.on('pointerdown', dismiss);
  }

  /**
   * Every recipe in the book, cooked in one run (§5.5).
   *
   * The run does NOT end here — a child who has just proved they can read all eighteen cards should
   * not be stopped for it, and the dealer recycles on its own. It is a moment, held until they tap,
   * and it happens at most once a run.
   *
   * Built to the how-to card's panel, because that is the shape this game uses to say something in
   * words; the plates around the title are the only decoration, and they are the same 🍝 the treat
   * on the menu was.
   */
  private showChefCard(onDone: () => void): void {
    const layer = this.add.container(0, 0).setDepth(210);
    const shade = this.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.85,
    );
    shade.setInteractive();
    const panel = this.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH * 0.78, GAME_HEIGHT * 0.6, 0x1a1f25,
    ).setStrokeStyle(4, 0xffd166);
    const plates = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 150, '🍝 👨‍🍳 🍝', {
      fontFamily: FONT_STACK, fontSize: '52px',
    }).setOrigin(0.5);
    const title = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 60, i18next.t('recipe.chefTitle'), {
      fontFamily: scriptFontFamily(this.lang), fontSize: '58px', color: '#ffd166', fontStyle: 'bold',
    }).setOrigin(0.5);
    const body = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 30, i18next.t('recipe.chefBody'), {
      fontFamily: scriptFontFamily(this.lang), fontSize: '30px', color: '#ffffff', align: 'center',
      wordWrap: { width: GAME_WIDTH * 0.62 }, lineSpacing: 8,
    }).setOrigin(0.5);
    const cont = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 150, i18next.t('recipe.continue'), {
      fontFamily: FONT_STACK, fontSize: '26px', color: '#dfe6ee', padding: { x: 16, y: 9 },
    }).setOrigin(0.5);
    layer.add([shade, panel, plates, title, body, cont]);
    layer.setAlpha(0);
    this.tweens.add({ targets: layer, alpha: 1, duration: 300 });
    this.tweens.add({
      targets: plates, scale: { from: 0.7, to: 1 }, duration: 420, ease: 'Back.easeOut',
    });
    playRecordSound();

    // Tap anywhere, behind the same timestamp guard the how-to card uses and for the same reason:
    // the tap that served the last dish must not skip the card it just opened.
    const openedAt = performance.now();
    const dismiss = (): void => {
      if (performance.now() - openedAt < 500) return;
      this.input.off('pointerdown', dismiss);
      layer.destroy(true);
      onDone();
    };
    this.input.on('pointerdown', dismiss);
  }

  // ── end of run ───────────────────────────────────────────────────────────────

  private gameOver(autoLeaveMs = RECIPE.autoLeaveOverMs): void {
    if (this.over) return;
    this.over = true;
    this.locked = true;
    this.flameTimer?.remove();
    this.foldTimer?.remove();

    // Show what the card was asking for: the answer is the consolation for losing.
    this.folded = false;
    this.chipsBox.setAlpha(1);
    this.cardHint.setAlpha(0);
    // `active` is Phaser's "is this still alive" flag, and it is checked rather than trusted: the
    // list is kept clean above, and this is the second lock on the door — a throw here kills the
    // game loop, so it is the last place in this scene worth being defensive.
    for (const t of this.tiles) if (t.root.active) t.root.disableInteractive();

    trackRecipeEnd(this.dishes);   // web demo only

    const isRecord = this.dishes > this.best;
    if (isRecord) { setBestRecipe(this.dishes); this.best = this.dishes; playRecordSound(); }
    else playRoundCompleteSound();

    const headline = isRecord ? i18next.t('recipe.newBest') : i18next.t('recipe.gameOver');
    const message = `${headline}\n${i18next.t('recipe.cleared', { n: this.dishes })}`;
    const overlay = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, message, {
      fontFamily: FONT_STACK, fontSize: '60px',
      color: isRecord ? '#4ecdc4' : '#ffd166',
      padding: { x: 34, y: 20 }, align: 'center',
    }).setOrigin(0.5).setAlpha(0).setScale(0.8).setDepth(150);
    const overlayBg = addRoundedLabelBg(this, overlay, { radius: 24 });
    overlayBg.setAlpha(0).setScale(0.8);

    const cont = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 130, i18next.t('recipe.continue'), {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#dfe6ee', padding: { x: 16, y: 9 },
    }).setOrigin(0.5).setDepth(150);
    addRoundedLabelBg(this, cont);

    this.tweens.add({ targets: [overlay, overlayBg], alpha: 1, scale: 1, duration: 300, ease: 'Back.easeOut' });

    // Tap anywhere to return, behind the same timestamp guard as the how-to card, so the tap that
    // burned the last corn does not dismiss the card it just opened. And a timer as well as a tap:
    // this card is the end of the run, so it must be able to leave BY ITSELF — a child who does not
    // realise the screen wants tapping should never be stuck looking at it.
    const shownAt = performance.now();
    let left = false;
    const leaveNow = (): void => {
      if (left) return;
      left = true;
      this.input.off('pointerdown', onTap);
      this.scene.start('Menu');
    };
    // The 900ms guard belongs to the TAP and to nothing else: it exists so the tap that burned the
    // last corn cannot also dismiss the card it just opened. Putting the timer behind it too was a
    // way to strand the player all over again — the guard reads `performance.now()` while the timer
    // runs on the scene clock, so a clock running ahead of real time fires the auto-leave into a
    // closure that refuses, and then nothing is left to fire it.
    const onTap = (): void => {
      if (performance.now() - shownAt < 900) return;
      leaveNow();
    };
    this.input.on('pointerdown', onTap);
    this.time.delayedCall(autoLeaveMs, leaveNow);

    // End-of-game asks (web: buy card, apps: rating) — same pair, same order as its siblings, but
    // only on a card that stays long enough to read one. A burnt round is back at the menu in four
    // seconds; a buy card that appears at 1.5s, covers the dish count and vanishes at 4s is noise
    // charged to the one screen that was supposed to explain what just happened.
    if (autoLeaveMs >= RECIPE.autoLeaveOverMs) {
      maybeShowBuyPrompt(this, { delayMs: 1500 });
      maybeAskForRating(this, { kind: 'recipe', dishes: this.dishes });
    }
  }
}

/** Resolve a chosen background to its image asset slug (fable has day/night variants). */
function backgroundAsset(bg: Background): string {
  return bg === 'fable' ? fableVariant() : bg;
}
